/**
 * "Resolve with a fix note" in the Feedback inbox (9 Oct), as data.
 *
 * The Open / Resolved / All filter, the fix note the dialog sends, the
 * "PR #83" quick picks and what a row's status shows. Mirrors the backend's
 * `ResolutionFilter`, `FIX_NOTE_MAX` and `Resolution`. Pure and import-free,
 * so `node --test` can import it directly.
 */

export type StatusFilter = "open" | "resolved" | "all";

/** The chips, in display order. Open is the default. */
export const STATUS_FILTERS: readonly StatusFilter[] = ["open", "resolved", "all"];

export const DEFAULT_STATUS_FILTER: StatusFilter = "open";

/** Matches the backend's `FIX_NOTE_MAX`. */
export const FIX_NOTE_MAX = 500;

/** Most PR quick picks offered. */
export const MAX_PR_PICKS = 6;

/** The `?status=` value, or the default (Open) when missing or unknown. */
export function statusFilterOf(raw: string | null | undefined): StatusFilter {
  return raw === "resolved" || raw === "all" || raw === "open" ? raw : DEFAULT_STATUS_FILTER;
}

/**
 * The fix note as sent: one line, trimmed, capped, without the dangling
 * " –" a quick pick leaves when nothing was written after it.
 */
export function normaliseFixNote(text: string): string {
  return text
    .split(/\s+/)
    .filter(Boolean)
    .join(" ")
    .replace(/\s*[–-]+$/, "")
    .slice(0, FIX_NOTE_MAX);
}

/** "PR #83" for 83. */
export function prLabel(prNumber: number): string {
  return `PR #${prNumber}`;
}

/** The PR numbers in some notes ("PR #83", "pr#90"), first mention first, once each. */
export function prNumbersIn(notes: readonly string[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const note of notes) {
    for (const match of note.matchAll(/\bPR\s*#\s*(\d+)/gi)) {
      const value = Number(match[1]);
      if (!seen.has(value)) {
        seen.add(value);
        out.push(value);
      }
    }
  }
  return out;
}

/**
 * The quick picks: recent PRs, newest first, each as "PR #<n>", at most
 * `MAX_PR_PICKS`. The server's list comes first; numbers found in the notes
 * fill in.
 */
export function prQuickPicks(pullRequests: readonly number[], notes: readonly string[]): string[] {
  const seen = new Set<number>();
  const out: string[] = [];
  for (const value of [...pullRequests, ...prNumbersIn(notes)]) {
    if (Number.isInteger(value) && value > 0 && !seen.has(value)) {
      seen.add(value);
      out.push(prLabel(value));
    }
  }
  return out.slice(0, MAX_PR_PICKS);
}

/**
 * Put a quick pick in front of the note: "PR #83 – " on an empty note,
 * "PR #83 – what was there" otherwise, unchanged when it already starts so.
 */
export function applyQuickPick(current: string, pick: string): string {
  const note = current.trim();
  if (note === "") {
    return `${pick} – `;
  }
  if (note.toLowerCase().startsWith(pick.toLowerCase())) {
    return current;
  }
  return `${pick} – ${note}`;
}

export interface ResolutionLike {
  status?: "open" | "resolved" | null;
  came_back?: boolean | null;
  fix_note?: string | null;
}

/** What the Status column shows: open, resolved, or came back after a fix. */
export type ResolutionState = "open" | "resolved" | "cameBack";

export function resolutionState(resolution: ResolutionLike | null | undefined): ResolutionState {
  if (resolution?.status === "resolved") {
    return "resolved";
  }
  return resolution?.came_back ? "cameBack" : "open";
}

/** Open vs resolved for the chips' labels; zeros while the counts are unknown. */
export function chipCount(
  filter: StatusFilter,
  counts: { open?: number | null; resolved?: number | null } | null | undefined,
): number {
  const open = counts?.open ?? 0;
  const resolved = counts?.resolved ?? 0;
  if (filter === "open") {
    return open;
  }
  return filter === "resolved" ? resolved : open + resolved;
}
